// plan-11 S07: touch interaction helpers — bible §10, §15

/**
 * Attaches a long-press handler for touch devices.
 * Fires callback if the touch is held for `durationMs` without moving > 8px.
 * Returns an unsubscribe function.
 */
export function onLongPress(
  el: HTMLElement,
  cb: (e: TouchEvent) => void,
  durationMs: number = 350,
): () => void {
  let timerId: ReturnType<typeof setTimeout> | null = null
  let startX = 0
  let startY = 0

  function onTouchStart(e: TouchEvent): void {
    const touch = e.touches[0]
    if (!touch) return
    startX = touch.clientX
    startY = touch.clientY
    timerId = setTimeout(() => {
      timerId = null
      cb(e)
    }, durationMs)
  }

  function onTouchMove(e: TouchEvent): void {
    const touch = e.touches[0]
    if (!touch) return
    const dx = Math.abs(touch.clientX - startX)
    const dy = Math.abs(touch.clientY - startY)
    if (dx > 8 || dy > 8) {
      cancel()
    }
  }

  function onTouchEnd(): void {
    cancel()
  }

  function onTouchCancel(): void {
    cancel()
  }

  function cancel(): void {
    if (timerId !== null) {
      clearTimeout(timerId)
      timerId = null
    }
  }

  el.addEventListener('touchstart', onTouchStart, { passive: true })
  el.addEventListener('touchmove', onTouchMove, { passive: true })
  el.addEventListener('touchend', onTouchEnd, { passive: true })
  el.addEventListener('touchcancel', onTouchCancel, { passive: true })

  return () => {
    cancel()
    el.removeEventListener('touchstart', onTouchStart)
    el.removeEventListener('touchmove', onTouchMove)
    el.removeEventListener('touchend', onTouchEnd)
    el.removeEventListener('touchcancel', onTouchCancel)
  }
}
